import { environment } from '../../../environments/environment.js';
import { CoinflowChargeParams } from '../../core/coinflow-checkout.service.js';

/**
 * Same shape as the checkout client's params; sessionKey is nullable because
 * the gaming session token resolves async after component init.
 */
export type WalletPayChargeContext = Omit<CoinflowChargeParams, 'sessionKey'> & { sessionKey: string | null };

export interface WalletPayHandlers {
  /** Called with the Coinflow paymentId; the flow registers it with ITS pending-payment service. */
  onPaymentRegistered(paymentId: string): void;
  /** Coinflow decline surfacing (mapped to the button's `onError`). Omit = silent declines. */
  onDecline?(message: string): void;
  /** Normalized from Coinflow's handleHeightChange; open = height > 50. */
  onSheetHeightChange(open: boolean, height: number): void;
}

/**
 * Gaming-only USDC-on-Solana settlement rails. Typed and opt-in: merch never
 * passes it, so fiat-default settlement for merch is structurally guaranteed.
 */
export interface WalletPaySettlement {
  settlementType: 'USDC';
  blockchain: 'solana';
}

/**
 * Props for the Coinflow Apple Pay / Google Pay buttons (both consume the
 * identical purchaseProps contract). The builder owns only the invariant keys;
 * it never defaults a charge key and never merges into webhookInfo — the
 * merchant/session/amount/webhook composition must stay with the flows, same
 * bar as CoinflowCheckoutService. Returns a loose object: Coinflow's published
 * prop typings lag its accepted prop set, so consumers bind with an `as any`
 * cast.
 */
export function buildWalletPayProps(args: {
  charge: WalletPayChargeContext;
  deviceId: string | undefined;
  email: string | null | undefined;
  handlers: WalletPayHandlers;
  settlement?: WalletPaySettlement;
}): object {
  const { charge, deviceId, email, handlers, settlement } = args;
  const { onDecline } = handlers;

  return {
    webhookInfo: charge.webhookInfo,
    merchantId: charge.merchantId,
    env: environment.coinflow.env,
    ...(settlement
      ? {
          settlementType: settlement.settlementType,
          blockchain: settlement.blockchain
        }
      : {}),
    onSuccess: (result: { paymentId: string }) => handlers.onPaymentRegistered(result.paymentId),
    ...(onDecline ? { onError: (message: string) => onDecline(message) } : {}),
    authOnly: true,
    supportEmail: environment.coinflow.supportEmail,
    deviceId,
    color: 'white',
    email,
    subtotal: { currency: 'USD', cents: charge.subtotalCents },
    sessionKey: charge.sessionKey,
    chargebackProtectionData: charge.chargebackProtectionData,
    ...(charge.customerInfo ? { customerInfo: charge.customerInfo } : {}),
    handleHeightChange: (height: string) => handlers.onSheetHeightChange(Number(height) > 50, Number(height))
  };
}
